import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Abi, Address, Hash } from 'viem';
import { useConfig } from 'wagmi';
import { getBlockNumber, simulateContract, waitForTransactionReceipt, writeContract } from 'wagmi/actions';
import { protocolErrorsAbi } from '@syntra/abi';
import { useI18n } from '../../i18n/index.tsx';
import { describeError } from './errors.ts';
import { refreshAfter } from './refresh.ts';

export type TxPhase = 'idle' | 'confirm' | 'pending' | 'done' | 'failed';

export interface TxRequest {
  address: Address;
  abi: Abi;
  functionName: string;
  args?: readonly unknown[];
  value?: bigint;
}

/**
 * Simulate (so reverts surface with a reason before the wallet opens), write, wait, then refresh every query once the
 * read RPC has the transaction's block (see refresh.ts); the action is `done` only after that first refresh.
 * The protocol error catalogue is merged into each ABI so errors raised by a nested contract decode too.
 */
export function useTx() {
  const config = useConfig();
  const qc = useQueryClient();
  const { t } = useI18n();
  const [phase, setPhase] = useState<TxPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [errorName, setErrorName] = useState<string | null>(null); // the protocol error behind `error`, if any
  const [hash, setHash] = useState<Hash | null>(null);

  const run = useCallback(
    async (req: TxRequest): Promise<Hash | null> => {
      setError(null);
      setErrorName(null);
      setHash(null);
      try {
        setPhase('confirm');
        const abi = [...req.abi, ...protocolErrorsAbi] as Abi;
        const { request } = await simulateContract(config, { ...req, abi } as never);
        const h = await writeContract(config, request as never);
        setHash(h);
        setPhase('pending');
        const receipt = await waitForTransactionReceipt(config, { hash: h });
        if (receipt.status !== 'success') throw new Error('The transaction reverted.');
        await refreshAfter(() => qc.invalidateQueries(), {
          block: receipt.blockNumber,
          head: () => getBlockNumber(config, { cacheTime: 0 }),
        });
        setPhase('done');
        return h;
      } catch (e) {
        const d = describeError(e, t);
        setError(d.text);
        setErrorName(d.name ?? null);
        setPhase('failed');
        return null;
      }
    },
    [config, qc, t],
  );

  const reset = useCallback(() => {
    setPhase('idle');
    setError(null);
    setErrorName(null);
    setHash(null);
  }, []);

  return { run, phase, error, errorName, hash, reset, busy: phase === 'confirm' || phase === 'pending' };
}
